import React from 'react';
import { Page, Layout } from '@shopify/polaris';
import { SyncSummary, MessageQueue, HelpSection, PayoutReviewPanel } from '../common';
import { Card, BlockStack, Text, InlineStack, Button, Spinner, InlineCode, Box } from '@shopify/polaris';
import { EditIcon, DeleteIcon, PlayIcon, PauseCircleIcon } from '@shopify/polaris-icons';
import { ManualSync } from '../common/ManualSync';
import { ScheduleModal } from '../modals/ScheduleModal';
import { useSyncStatus, useSchedule, useLogs } from '../../hooks';
import { flowApi } from '../../services/api/flowApi';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { formatServiceLabel } from '../../utils/serviceLabels';

export function EntityTab({ service, ui, label, mode }) {
  const [scheduleModalOpen, setScheduleModalOpen] = React.useState(false);
  const [webhookInfo, setWebhookInfo] = React.useState(null);
  const [lastWebhook, setLastWebhook] = React.useState(null);
  const [manualSyncNotice, setManualSyncNotice] = React.useState('');
  const manualSyncRef = React.useRef(null);
  const { currentWorkspace } = useWorkspace();

  const {
    status,
    lastSync,
    startSync,
    stopSync,
    refreshStatus,
    isLoading: syncLoading,
    error: syncError,
    progress
  } = useSyncStatus(service);

  const {
    schedule,
    updateSchedule,
    removeSchedule,
    toggleScheduleEnabled,
    isLoading: scheduleLoading
  } = useSchedule(service);

  const {
    logs,
    errors,
    refreshLogs,
    clearLogs,
    isLoading: logsLoading,
    pagination,
    handlePageChange,
    handlePageSizeChange,
    errorPagination,
    handleErrorPageChange,
    searchValue,
    isSearchActive,
    handleSearchChange,
    submitSearch,
    clearSearch
  } = useLogs(service);

  React.useEffect(() => {
    let isMounted = true;
    if (mode === 'webhook') {
      flowApi.getWebhookInfo(service)
        .then((resp) => { if (isMounted && resp?.success) setWebhookInfo(resp); })
        .catch(() => {});
      // Poll last webhook payload presence
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const fetchLast = async () => {
        try {
          const wsId = localStorage.getItem('selectedWorkspace') ? JSON.parse(localStorage.getItem('selectedWorkspace')).id : null;
          if (!wsId) return;
          const resp = await fetch(`${apiBase}/webhook/${wsId}/${service}/last`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('tenantAccessToken') || localStorage.getItem('accessToken')}` } });
          if (resp.ok) {
            const data = await resp.json();
            if (isMounted) setLastWebhook(data);
          }
        } catch (_) {}
      };
      fetchLast();
      const timer = setInterval(fetchLast, 5000);
      return () => { isMounted = false; clearInterval(timer); };
    }
    return () => { isMounted = false; };
  }, [service, mode]);

  const handleManualSync = async (payload) => {
    setManualSyncNotice('');
    await startSync(payload);
  };

  const frontendProvider = String(currentWorkspace?.integrations?.frontend?.provider || '').toLowerCase();
  const isWayfairAgentFulfilledOrders = service === 'fulfilled_orders' && frontendProvider === 'wayfairagent';
  const isWayfairAgentPayouts = service === 'payouts' && frontendProvider === 'wayfairagent';
  const isWayfairAgentPayoutSettlements = service === 'payout_settlements' && frontendProvider === 'wayfairagent';

  const handleSummaryStart = React.useCallback(() => {
    return startSync();
  }, [startSync]);

  const handleReviewRetry = React.useCallback(async (payload) => {
    const result = await startSync(payload);
    const count = Array.isArray(payload?.entityIds) ? payload.entityIds.length : 0;
    setManualSyncNotice(`${count} review ${count === 1 ? 'record' : 'records'} queued for ${formatServiceLabel(service)}.`);
    return result;
  }, [service, startSync]);

  const handleScheduleToggle = async () => {
    try {
      const newEnabledState = !schedule?.enabled;
      await toggleScheduleEnabled(newEnabledState);
    } catch (error) {
    }
  };

  return (
    <Page title={`${ui?.pageTitle || label || formatServiceLabel(service)} Synchronization`}>
      <Layout>
        <Layout.Section>
          <SyncSummary
            service={service}
            onStart={handleSummaryStart}
            onStop={stopSync}
            onRefresh={refreshStatus}
            isLoading={syncLoading}
            error={syncError}
            statusProp={status}
            lastSyncProp={lastSync}
            progressProp={progress}
          />
        </Layout.Section>

        {/* Schedule Status */}
        {mode !== 'webhook' && schedule && (
          <Layout.Section>
            <Card>
              <BlockStack gap="loose">
                <div className="card-title-container">
                  <Text variant="headingMd" fontWeight="bold" className="card-title-standard">
                    Current Schedule
                  </Text>
                  {scheduleLoading && (
                    <InlineStack gap="base" align="center">
                      <Spinner size="small" />
                      <Text variant="bodySm" color="subdued">Loading schedule...</Text>
                    </InlineStack>
                  )}
                  <InlineStack gap="base">
                    <Button
                      icon={EditIcon}
                      variant="tertiary"
                      onClick={() => setScheduleModalOpen(true)}
                      disabled={scheduleLoading}
                    >
                      Edit Schedule
                    </Button>
                    <Button
                      icon={schedule?.enabled !== false ? PauseCircleIcon : PlayIcon}
                      variant="tertiary"
                      tone={schedule?.enabled !== false ? 'critical' : 'success'}
                      onClick={handleScheduleToggle}
                      disabled={scheduleLoading}
                    >
                      {schedule?.enabled !== false ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      icon={DeleteIcon}
                      variant="tertiary"
                      onClick={async () => { await removeSchedule(); }}
                      disabled={scheduleLoading}
                    >
                      Remove
                    </Button>
                  </InlineStack>
                </div>

                <div className="schedule-container">
                  <div className="schedule-header">
                    <div className="schedule-header-cell">Interval</div>
                    <div className="schedule-header-cell">Start Time</div>
                    <div className="schedule-header-cell">End Time</div>
                    <div className="schedule-header-cell">Days</div>
                  </div>

                  <div className="schedule-grid grid-layout grid-4-columns">
                    <div className="schedule-item"><Text variant="bodyMd" className="schedule-value">{schedule.metadata?.frequency || 'Custom schedule'}</Text></div>
                    <div className="schedule-item"><Text variant="bodyMd" className="schedule-value">{schedule.metadata?.startTime || 'Not set'}</Text></div>
                    <div className="schedule-item"><Text variant="bodyMd" className="schedule-value">{schedule.metadata?.endTime || 'Not configured'}</Text></div>
                    <div className="schedule-item"><Text variant="bodyMd" className="schedule-value">{schedule.metadata?.days ? schedule.metadata.days.join(', ') : 'Not set'}</Text></div>
                  </div>
                </div>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* No Schedule Set */}
        {mode !== 'webhook' && !schedule && (
          <Layout.Section>
            <Card>
              <BlockStack gap="loose" align="center">
                <Text variant="bodyMd" color="subdued">No automatic sync schedule configured</Text>
                <Button variant="primary" onClick={() => setScheduleModalOpen(true)} disabled={scheduleLoading}>Set Up Schedule</Button>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {mode === 'webhook' && (
          <Layout.Section>
            <Card>
              <BlockStack gap="loose">
                <div className="card-title-container">
                  <Text variant="headingMd" fontWeight="bold" className="card-title-standard">
                    Webhook Configuration
                  </Text>
                </div>
                {webhookInfo ? (
                  <BlockStack gap="tight">
                    {/* Endpoint */}
                    <BlockStack gap="tight">
                      <Text variant="bodyMd">Endpoint URL</Text>
                      <Box padding="200" background="bg-surface-secondary" borderRadius="100">
                        <InlineStack align="space-between">
                          <InlineCode>{webhookInfo.url}</InlineCode>
                          <Button size="slim" onClick={() => navigator.clipboard.writeText(webhookInfo.url)}>Copy</Button>
                        </InlineStack>
                      </Box>
                    </BlockStack>

                    {/* Required headers */}
                    {Array.isArray(webhookInfo.headers) && webhookInfo.headers.length > 0 && (
                      <BlockStack gap="tight">
                        <Text variant="bodyMd">Required headers</Text>
                        {webhookInfo.headers.map((h, idx) => (
                          <Box key={idx} padding="200" background="bg-surface-secondary" borderRadius="100">
                            <InlineStack align="space-between">
                              <InlineCode>{`${h.name}: ${h.value}`}</InlineCode>
                              <Button size="slim" onClick={() => navigator.clipboard.writeText(`${h.name}: ${h.value}`)}>Copy</Button>
                            </InlineStack>
                          </Box>
                        ))}
                      </BlockStack>
                    )}
                    {/* Spacing */}
                    <Box style={{ height: '14px' }} />
                    {/* Bottom row: Provider (left) + Status (center) + Button (right) */}
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="bodySm" color="subdued">Provider: {webhookInfo.provider || 'unknown'}</Text>
                      {lastWebhook?.exists && (
                        <Text variant="bodySm" color="subdued">
                          {`Payload received${lastWebhook.receivedAt ? ' ' + new Date(lastWebhook.receivedAt).toLocaleString() : ''}`}
                        </Text>
                      )}
                      {lastWebhook?.exists && (
                        <Button size="slim" onClick={async () => {
                          try {
                            const { configApi } = await import('../../services/api/configApi');
                            const resp = await configApi.promoteSampleFromWebhook(service);
                          } catch (e) {}
                        }}>Use as sample</Button>
                      )}
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <Text variant="bodyMd" color="subdued">This flow is webhook-driven. Use Secure Connections to configure credentials. The endpoint is managed by the backend.</Text>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {(isWayfairAgentPayouts || isWayfairAgentPayoutSettlements) && (
          <Layout.Section>
            <PayoutReviewPanel
              service={service}
              onRetry={handleReviewRetry}
              retryDisabled={syncLoading || status === 'starting' || status === 'running'}
            />
          </Layout.Section>
        )}

        <Layout.Section>
          <MessageQueue
            logs={logs}
            errors={errors}
            isLoading={logsLoading}
            onRefresh={refreshLogs}
            onClear={clearLogs}
            service={service}
            pagination={pagination}
            errorPagination={errorPagination}
            onPageChange={handlePageChange}
            onErrorPageChange={handleErrorPageChange}
            onPageSizeChange={handlePageSizeChange}
            searchValue={searchValue}
            isSearchActive={isSearchActive}
            onSearchChange={handleSearchChange}
            onSearchSubmit={submitSearch}
            onSearchClear={clearSearch}
          />
        </Layout.Section>

        <Layout.Section>
          <div ref={manualSyncRef}>
          <ManualSync
            service={service}
            onSync={handleManualSync}
            isLoading={syncLoading || status === 'starting' || status === 'running'}
            error={syncError}
            entityLabel={isWayfairAgentPayoutSettlements
              ? 'Wayfair Settlement Reference'
              : (isWayfairAgentFulfilledOrders || isWayfairAgentPayouts)
                ? 'Wayfair PO Number(s)'
                : ui?.entityLabel}
            placeholder={isWayfairAgentPayoutSettlements
              ? 'e.g., 10002005923975'
              : (isWayfairAgentFulfilledOrders || isWayfairAgentPayouts)
                ? 'e.g., CS665179344'
                : ui?.entityPlaceholder}
            notice={manualSyncNotice}
          />
          </div>
        </Layout.Section>

        <Layout.Section>
          <HelpSection />
        </Layout.Section>

        <ScheduleModal
          open={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          onSave={async (data) => {
            await updateSchedule(data);
            setScheduleModalOpen(false);
          }}
          currentSchedule={schedule}
          isLoading={scheduleLoading}
        />
      </Layout>
    </Page>
  );
}
